/**
 * Ontario forms readiness — the counting rules, in ONE place.
 *
 * GOVID-READINESS-PRIVATE-STATE-1 (s201; Option C ruled at s200).
 *
 * The readiness surface has THREE row states and there is no fourth:
 *
 *   • **present**        — we hold the value and can show it.
 *   • **missing**        — the client has not entered it, and is asked to.
 *   • **held privately** — the row is real and named, but the value lives in the client's
 *                          secure file and never reaches the server. 🟢 The nine Government-ID
 *                          rows read this because `id_number` is Category A.
 *
 * 🔴 The load-bearing half is the COUNTING, not the label. A held-privately row is counted
 * nowhere — not in the denominator, not in the numerator, and never as a "missing field" the
 * client is told to go and fix. A page that prints "held privately" while still counting the row
 * against the client has not shipped Option C.
 *
 * This module exists because THREE surfaces render the same numbers — the HTML export page, the
 * PDF export, and the executor dashboard's readiness card — and a fourth (the per-form badge)
 * recomputes them per form. One computation, every caller (the D-156 shape).
 */

/** One readiness row. `heldPrivately` is the third state; absent/false means the ordinary two. */
export interface ReadinessField {
  label: string;
  value: string | null;
  isComplete: boolean;
  heldPrivately?: boolean;
}

export interface ReadinessForm {
  id?: string;
  title: string;
  fields: ReadinessField[];
}

/**
 * Does this row count toward either side of the ratio?
 *
 * 🔴 The parameter takes `label` as well as the flag deliberately: a type whose properties are ALL
 * optional is a "weak type" in TypeScript, and passing a row that happens not to carry
 * `heldPrivately` would then fail to typecheck. Requiring `label` — which every readiness row has —
 * keeps the predicate callable from every surface.
 */
export function isCountedReadinessField(field: { label: string; heldPrivately?: boolean }): boolean {
  return field.heldPrivately !== true;
}

export interface ReadinessSummary {
  totalFields: number;
  filledFields: number;
  missingCount: number;
  missingFields: string[];
}

/** The whole summary, computed once. `missingFields` reads "<form title> → <field label>". */
export function summarizeReadiness(forms: readonly ReadinessForm[]): ReadinessSummary {
  let totalFields = 0;
  let filledFields = 0;
  const missingFields: string[] = [];

  for (const form of forms) {
    for (const field of form.fields) {
      if (!isCountedReadinessField(field)) continue;
      totalFields += 1;
      if (field.value) filledFields += 1;
      else missingFields.push(`${form.title} → ${field.label}`);
    }
  }

  return { totalFields, filledFields, missingCount: missingFields.length, missingFields };
}

/** The per-form badge's two numbers, under the same rule as the page total. */
export function countFormFields(form: ReadinessForm): { filled: number; total: number } {
  const counted = form.fields.filter(isCountedReadinessField);
  return { filled: counted.filter((f) => f.value).length, total: counted.length };
}
